import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useOutletContext, useSearchParams, useParams } from 'react-router-dom';
import { parseISO, addMonths, subMonths, getYear, getMonth } from 'date-fns';
import { Helmet } from 'react-helmet-async';

import FilterBar from '../components/FilterBar/FilterBar';
import Calendar from '../components/Calendar/Calendar';
import EventListSidebar from '../components/EventList/EventListSidebar';
import EventPreview from '../components/EventPreview/EventPreview';
import MapView from '../components/MapView/MapView';
import './HomePage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const HomePage = () => {
  const [allEvents, setAllEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedEventForPreview, setSelectedEventForPreview] = useState(null);
  const [view, setView] = useState('map'); 
  
  const [mapState, setMapState] = useState({
    center: { lat: 20, lng: 0 },
    zoom: 2,
  });

  const { isSidebarVisible } = useOutletContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { slug } = useParams();

  const displayedMonth = useMemo(() => {
    const yearParam = searchParams.get('year');
    const monthParam = searchParams.get('month');
    if (yearParam && monthParam) {
      return new Date(parseInt(yearParam, 10), parseInt(monthParam, 10) - 1, 1);
    }
    return new Date();
  }, [searchParams]);

  const countryOptions = useMemo(() => {
    const uniqueCountries = [...new Set(allEvents.map(event => event.country).filter(Boolean))];
    uniqueCountries.sort();
    return uniqueCountries.map(country => ({ value: country, label: country }));
  }, [allEvents]);

  const categoryOptions = useMemo(() => {
    return categories.map(c => ({ value: c.name, label: `${c.icon} ${c.name}` }));
  }, [categories]);

  useEffect(() => {
    const fetchEventForPreview = async (eventSlug) => {
      try {
        const response = await fetch(`${API_URL}/events/slug/${eventSlug}`);
        if (!response.ok) {
          navigate(`/?${searchParams.toString()}`, { replace: true });
          return;
        }
        const eventData = await response.json();
        setSelectedEventForPreview(eventData);
      } catch (error) {
        console.error("Failed to fetch event for preview:", error);
        navigate(`/?${searchParams.toString()}`, { replace: true });
      }
    };
    if (slug) { fetchEventForPreview(slug); } 
    else { setSelectedEventForPreview(null); }
  }, [slug, navigate, searchParams]);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [eventsResponse, categoriesResponse] = await Promise.all([
          fetch(`${API_URL}/events`),
          fetch(`${API_URL}/categories`)
        ]);
        
        if (!eventsResponse.ok || !categoriesResponse.ok) throw new Error('Network response was not ok');
        
        const eventsData = await eventsResponse.json();
        const categoriesData = await categoriesResponse.json();
        
        const eventsWithDates = eventsData.map(event => ({ ...event, date: parseISO(event.event_date) }));
        setAllEvents(eventsWithDates);
        setCategories(categoriesData);
      } catch (error) { console.error("Failed to fetch initial data:", error); }
    };
    fetchAllData();
  }, []); 

  useEffect(() => {
    const countryParams = searchParams.getAll('country');
    const categoryParams = searchParams.getAll('category');
    const continentParams = searchParams.getAll('continent');
    const monthParam = searchParams.get('month');

    let eventsToDisplay = allEvents;
    
    if (categoryParams.length > 0) { eventsToDisplay = eventsToDisplay.filter(e => categoryParams.includes(e.category)); }
    if (continentParams.length > 0) { eventsToDisplay = eventsToDisplay.filter(e => continentParams.includes(e.continent)); }
    if (countryParams.length > 0) { eventsToDisplay = eventsToDisplay.filter(e => countryParams.includes(e.country)); }
    if (monthParam) { eventsToDisplay = eventsToDisplay.filter(e => e.date.getMonth() + 1 === parseInt(monthParam, 10)); }

    setFilteredEvents(eventsToDisplay);
  }, [searchParams, allEvents]);
  
  const handleFilterChange = (key, selectedOptions) => {
    const newValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete(key);
    if (newValues.length > 0) {
      newValues.forEach(v => newSearchParams.append(key, v));
    }
    setSearchParams(newSearchParams);
  };
  
  const handleMonthUrlChange = (newDate) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('year', getYear(newDate).toString());
    newSearchParams.set('month', (getMonth(newDate) + 1).toString());
    setSearchParams(newSearchParams);
  };

  const handleMonthFilterChange = (event) => {
    const month = event.target.value;
    const newSearchParams = new URLSearchParams(searchParams);
    if (month) {
      newSearchParams.set('month', month);
      newSearchParams.set('year', getYear(displayedMonth).toString());
    } else {
      newSearchParams.delete('month');
      newSearchParams.delete('year');
    }
    setSearchParams(newSearchParams);
  };
  
  const handleGoToToday = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('year');
    newSearchParams.delete('month');
    setSearchParams(newSearchParams);
  };

  const handleMapIdle = (map) => {
    if (map) {
      const newCenter = { lat: map.getCenter().lat(), lng: map.getCenter().lng() };
      const newZoom = map.getZoom();
      if (newCenter.lat !== mapState.center.lat || newCenter.lng !== mapState.center.lng || newZoom !== mapState.zoom) {
        setMapState({ center: newCenter, zoom: newZoom });
      }
    }
  };
  
  const handleEventSelect = (event) => navigate(`/events/${event.slug}?${searchParams.toString()}`);
  const handleClosePreview = () => navigate(`/?${searchParams.toString()}`);
  const prevMonth = () => handleMonthUrlChange(subMonths(displayedMonth, 1));
  const nextMonth = () => handleMonthUrlChange(addMonths(displayedMonth, 1));

  return (
    <>
      <Helmet>
        <title>VividYear</title>
        <meta name="description" content="Explore a calendar of exciting cultural, sports, and music events happening globally. Plan your next adventure with VividYear." />
      </Helmet>
      
      <div className="homepage-grid">
        <aside className={`sidebar-column ${isSidebarVisible ? 'visible' : 'hidden'}`}>
          <EventListSidebar events={filteredEvents} />
        </aside>

        <div className="calendar-column">
          <FilterBar
            countryOptions={countryOptions}
            categoryOptions={categoryOptions}
            onFilterChange={handleFilterChange}
            onMonthChange={handleMonthFilterChange}
            searchParams={searchParams}
            currentView={view}
            onViewChange={setView}
          />
          <div className="view-container">
            {view === 'calendar' ? (
              <Calendar
                events={filteredEvents}
                currentMonth={displayedMonth}
                onPrevMonth={prevMonth}
                onNextMonth={nextMonth}
                onEventSelect={handleEventSelect}
                onMonthChange={handleMonthUrlChange}
                onGoToToday={handleGoToToday}
              />
            ) : (
              <MapView 
                key={searchParams.toString()} 
                events={filteredEvents} 
                onMarkerClick={handleEventSelect}
                mapState={mapState}
                onMapIdle={handleMapIdle}
              />
            )}
          </div>
        </div>
      </div>

      <EventPreview 
        event={selectedEventForPreview} 
        onClose={handleClosePreview}
      />
    </>
  );
};

export default HomePage;